import {
	type OrderDetailResponseDto,
	OrderStatus,
	OrderTypeNotification,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useOrderEmailNotificationSend } from "Order/Api/Mutation/sendOrderNotificationEmailMutation";
import { useUnfinishOrder } from "Order/Hooks/useUnfinishOrder";
import { isClaimDetailOpen } from "Order/Modules/Claim/Atom/claimDetailOpen";
import { isArchiveOrderConfirmationModalOpenAtom } from "Order/Modules/Examination/Atom/archiveOrderConfirmationModalAtom";
import { isCreateExaminationModalOpenAtom } from "Order/Modules/Examination/Atom/createExaminationModalAtom";
import { useRegenerateInvoice } from "Order/Modules/Invoice/Api/Mutation/useRegenerateInvoiceMutation";
import { useOrderFeeQuery } from "Order/Modules/Payment/Api/Query/useOrderFeeQuery";
import {
	Delete,
	FireExtinguisher,
	HelpOutline,
	MoreVert,
	Refresh,
	Restore,
	Send,
} from "@mui/icons-material";
import { Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { useAtom } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

const createClaimOrderStatuses = new Set([
	OrderStatus.done,
	OrderStatus.in_progress,
]);
const createExaminationOrderStatuses = new Set([
	OrderStatus.pending,
	OrderStatus.confirmed,
]);

type ActionMenuProps = {
	order: OrderDetailResponseDto;
};

type MenuItemType =
	| {
			onClick: () => void;
			disabled: boolean;
			icon: JSX.Element;
			label: string;
	  }
	| "divider";

const { headerButtons } = Resources.base.order;
const partnerInfoNotificationType = "partner_info" as OrderTypeNotification;

export function ActionsMenu({ order }: ActionMenuProps) {
	const { t } = useResource();
	const { unfinishOrder } = useUnfinishOrder(order.id, order.partner?.id);
	const { id: orderId } = useParams();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [, setIsExaminationModalOpen] = useAtom(
		isCreateExaminationModalOpenAtom,
	);
	const [, setIsArchiveModalOpen] = useAtom(
		isArchiveOrderConfirmationModalOpenAtom,
	);
	const { data: orderFee } = useOrderFeeQuery(order.id);
	const hasDeposit = useMemo(
		() => (orderFee ? orderFee?.depositRequirementAmount > 0 : false),
		[orderFee],
	);

	const handleUnfinishOrder = useCallback(() => {
		unfinishOrder();
		closeMenu();
	}, [unfinishOrder]);

	const [, setIsClaimOpen] = useAtom(isClaimDetailOpen);
	const optionsButtonRef = useRef<HTMLButtonElement | null>(null);
	const { mutate: sendOrderEmailNotification } =
		useOrderEmailNotificationSend();

	const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);
	const closeMenu = useCallback(() => setIsMenuOpen(false), []);
	const { mutate: regenerateInvoice, isPending: isPendingRegenerateInvoice } =
		useRegenerateInvoice(order!.id);

	const handleRegenerateInvoice = () => {
		regenerateInvoice({});
	};

	const openClaimDetail = useCallback(() => {
		setIsClaimOpen(true);
		closeMenu();
	}, [setIsClaimOpen, closeMenu]);

	const openExaminationModal = useCallback(() => {
		setIsExaminationModalOpen(true);
		closeMenu();
	}, [setIsExaminationModalOpen, closeMenu]);

	const openArchiveModal = useCallback(() => {
		setIsArchiveModalOpen(true);
		closeMenu();
	}, [setIsArchiveModalOpen, closeMenu]);

	const handleSendEmailNotification = useCallback(
		(type: OrderTypeNotification) => {
			sendOrderEmailNotification({ data: { type }, orderId: Number(orderId) });
			closeMenu();
		},
		[sendOrderEmailNotification, orderId, closeMenu],
	);

	const getMenuItems = (): MenuItemType[] => [
		{
			onClick: () =>
				handleSendEmailNotification(OrderTypeNotification.customer),
			disabled: false,
			icon: <Send />,
			label: t(headerButtons.notifyCustomer),
		},
		{
			onClick: () => handleSendEmailNotification(OrderTypeNotification.partner),
			disabled: !order?.partner?.email,
			icon: <Send />,
			label: t(headerButtons.notifyPartner),
		},
		{
			onClick: () => handleSendEmailNotification(partnerInfoNotificationType),
			disabled: !order.partner,
			icon: <Send />,
			label: t(headerButtons.sendPartnerInfo),
		},
		{
			onClick: () => handleSendEmailNotification(OrderTypeNotification.deposit),
			disabled: !hasDeposit,
			icon: <Send />,
			label: t(headerButtons.deposit),
		},
		"divider",

		{
			onClick: () => handleRegenerateInvoice(),
			disabled: order.status !== OrderStatus.done || isPendingRegenerateInvoice,
			icon: <Refresh />,
			label: order?.invoiceId
				? "Regenerate Payment Request"
				: "Generate Payment Request",
		},
		{
			onClick: () => handleUnfinishOrder(),
			disabled:
				order.sfInvoicePeriod === null || order.status !== OrderStatus.done,
			icon: <Restore />,
			label: "Unfinish order",
		},
		{
			onClick: openExaminationModal,
			disabled:
				!createExaminationOrderStatuses.has(order.status) ||
				order.hasExamination,
			icon: <HelpOutline />,
			label: t(headerButtons.createExamination),
		},
		{
			onClick: openClaimDetail,
			disabled:
				!createClaimOrderStatuses.has(order.status) || order.claims.length > 0,
			icon: <FireExtinguisher />,
			label: t(headerButtons.claim),
		},
		{
			onClick: openArchiveModal,
			disabled: order.status !== OrderStatus.pending, // only pending orders can be archived
			icon: <Delete />,
			label: t(headerButtons.archive),
		},
	];

	return (
		<>
			<IconButton
				ref={optionsButtonRef}
				onClick={toggleMenu}
				aria-expanded={isMenuOpen ? "true" : undefined}
			>
				<MoreVert />
			</IconButton>
			<Menu
				anchorEl={optionsButtonRef.current}
				elevation={0}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				open={isMenuOpen}
				onClose={closeMenu}
			>
				{getMenuItems().map((item, index) =>
					item === "divider" ? (
						<Divider key={index} sx={{ my: 0.5 }} />
					) : (
						<MenuItem
							key={index}
							onClick={item.onClick}
							disableRipple
							disabled={item.disabled}
							sx={{ display: "flex", gap: 2 }}
						>
							{item.icon}
							{item.label}
						</MenuItem>
					),
				)}
			</Menu>
		</>
	);
}
